import React from "react";
import { transparentize } from "polished";
import { Sticky } from "react-sticky";
import styled, { css } from "styled-components";
import _ from "lodash";

import { useWindowSize } from "src/library/hooks";

import { LoadingSpinner } from "../LoadingSpinner";
import { Text } from "../Text";
import InfoBarItem, { IInfoItem } from "./InfoBarItem";

export interface IInfoBarProps {
  isLoading?: boolean;
  title: string;
  items: IInfoItem[];
  primaryAction?: JSX.Element | null;
  secondaryAction?: JSX.Element | null;
  disableSticky?: boolean;
  hideInformation?: boolean;
  listBorderBottom?: boolean;
  disableExpand?: boolean;
}

interface IInfoBarContentProps extends IInfoBarProps {
  expand: boolean;
  setExpand: (isExpanded: boolean) => void;
}

/**
 * Info bar component
 */
const InfoBar: React.ReactFCWithChildren<IInfoBarProps> = props => {
  const { width } = useWindowSize();
  const [ expand, setExpand ] = React.useState<boolean>(props.disableExpand ? true : false);
  const [ isMobile, setIsMobile ] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (!props.disableExpand) {
      // Set the isMobile state accordingly
      if (width && width < 1000 && !isMobile) {
        setIsMobile(true);
        setExpand(false);
      } else if (width && width >= 1000 && isMobile) {
        setIsMobile(false);
      }
    }
  }, [
    isMobile,
    width,
    props.disableExpand
  ]);

  // just render actions if information should be hidden
  if (props.hideInformation && (props.primaryAction || props.secondaryAction)) {
    return (
      (
        <ActionsWrapper className="actions-wrapper">
          {props.primaryAction && props.primaryAction}

          {props.secondaryAction && props.secondaryAction}
        </ActionsWrapper>
      )
    );
  }

  return (
    <Wrapper>
      {width && width < 1000 || props.disableSticky ? (
        <InfoBarContent
          {...props}
          expand={expand}
          setExpand={setExpand}
        />
      ) : (
        <Sticky
          topOffset={210}
          relative
        >
          {({ style }) => (
            <StickyWrapper style={style as React.CSSProperties}>
              <InfoBarContent
                {...props}
                expand={expand}
                setExpand={setExpand}
              />
            </StickyWrapper>
          )}
        </Sticky>
      )}
    </Wrapper>
  );
};

/**
 * Info bar content component
 */
const InfoBarContent: React.ReactFCWithChildren<IInfoBarContentProps> = props => {
  const {
    isLoading, title, items, primaryAction, secondaryAction, expand, setExpand, disableSticky, disableExpand, children, listBorderBottom
  } = props;

  const handleExpand = () => {
    if (expand) {
      return setExpand(false);
    }

    setExpand(true);
  };

  return (
    <Wrapper className="info-bar-content-wrapper">
      {(primaryAction || secondaryAction) && (
        <ActionsWrapper>
          {primaryAction && primaryAction}

          {secondaryAction && secondaryAction}
        </ActionsWrapper>
      )}

      <InfoWrapper
        className={`info-wrapper ${expand ? "expanded" : "collapsed"}`}
        disableSticky={disableSticky}
      >
        <Header>
          <Text
            fontSize="h3"
            weight="bold"
          >
            {_.startCase(_.toLower(title))}
          </Text>
        </Header>

        {isLoading && (
          <LoadingSpinner
            type="circular"
            offsetTop={50}
          />
        )}

        {!isLoading &&
          items.map((item, i) => {
            return (
              <InfoBarItem
                key={i}
                item={item}
                listBorderBottom={listBorderBottom ? i === items.length - 1 : undefined}
              />
            );
          })}

        {!isLoading && children}
      </InfoWrapper>

      {!disableExpand && (
        <ExpanderControllerStyled
          className="expander-controller-container"
          onClick={handleExpand}
        >
          <Text>
            {!expand ? "Expand" : "Collapse"}
          </Text>
        </ExpanderControllerStyled>
      )}
    </Wrapper>
  );
};

export { InfoBar };

const Wrapper = styled.div`
  transition: ${({ theme }) => theme.transition.quicker};
  display: flex;
  flex-direction: column;
  position: relative;
  order: 1;

  @media (min-width: 768px) {
    order: 2;
  }
`;

const StickyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
`;

const ActionsWrapper = styled.div`
  display: flex;
  height: auto;
  ${({ theme }) => css`
    padding: ${theme.spacing.padding.md} 0;
    border-bottom: 3px solid ${theme.colors.lightGrey};
    background: ${theme.colors.white};
    > * {
      margin: 0 ${theme.spacing.padding.md};
      flex-grow: 1;

      button { flex-grow: 1 };
    }

    
  `};
`;

const InfoWrapper = styled.div<Pick<IInfoBarProps, "disableSticky">>`
  position: relative;
  z-index: 2;
  height: 144px;
  overflow: scroll;
  -webkit-overflow-scrolling: touch;
  

  ${({ theme }) => css`
    background: ${theme.colors.white};
    color: ${theme.colors.primaryDark};
    box-shadow: 0 0 6px ${transparentize(0.9, theme.colors.darkGrey)};
  `};

  &.expanded {
    height: auto;
  }

  @media (min-width: 1000px) {
    min-height: 200px;
    ${({ disableSticky }) => !disableSticky ?
    css`
      height: calc(100vh - 140px);
      padding-bottom: 100px;
    ` :
    css` height: auto;`}
  }
`;

const ExpanderControllerStyled = styled.div`
  transition: ${({ theme }) => theme.transition.quick};
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 32px;
  background: #fdfdfd;
  border-radius: 0 0 100% 100%;
  cursor: pointer;
  margin-bottom: 40px;

  span {
    transition: ${({ theme }) => theme.transition.quickest};
    display: block;
    color: ${({ theme }) => theme.fonts.colors.secondary};
  }

  &:hover {
    span {
      color: ${({ theme }) => theme.colors.primaryLight};
    }
  }

  @media (min-width: 1000px) {
    display: none;
  }
`;

const Header = styled.div`
  ${({ theme }) => css`
    border-bottom: 1px solid ${theme.colors.lightestGrey};
    margin: ${theme.spacing.margins.sm};
    padding: ${theme.spacing.padding.sm};
  `};
`;
